/**
 * TeamAccessBoard — "Team Access". The unit lead's face of the access agent:
 * the team roster with each member's access, an approvals inbox, and a grant
 * composer. Board-first — the agent lives here, not in a chat transcript; chat
 * emits AccessProposalCard side-channel proposals that fold into this surface.
 *
 * Reads neutral and interdisciplinary on purpose (a nurse lead, a therapy lead,
 * a physician all use it) — no "nurse tool" framing. Presentational only: it
 * renders roster/requests/options and emits intent via callbacks (decide a
 * request, compose a grant, revoke a grant). The connected wrapper owns fetching,
 * loading states and optimistic/server-authoritative mutation policy.
 *
 * Order = priority: the approvals INBOX sits first (it is the actionable queue a
 * lead came to clear), then the grant COMPOSER, then the ROSTER for reference.
 */

import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AlarmClock,
  Inbox,
  UserRoundPlus,
  Users,
  X,
  type LucideIcon,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { EmptyState } from '@/components/clinical'
import { ExpiryChip, RoleBadge, SourceChip } from '@frontend/components/access/atoms'
import { resourceIcon, roleKey } from '@frontend/components/access/accessMeta'
import { PersonAvatar } from '@frontend/components/access/PersonAvatar'
import type {
  AccessGrant,
  AccessRequest,
  GrantDraft,
  GrantExpiry,
  ResourceRef,
  TeamMember,
} from '@frontend/components/access/types'

export type GrantSubjectOption = GrantDraft['subject']

export interface TeamAccessBoardProps {
  team: { label: string }
  members: TeamMember[]
  /** The approvals inbox — pending requests routed to this lead. */
  requests: AccessRequest[]
  /** Composer options — who can be granted, on what, at which role. */
  grantableSubjects?: GrantSubjectOption[]
  grantableResources?: ResourceRef[]
  grantableRoles?: string[]
  onDecide?: (requestId: string, decision: 'approve' | 'deny', justification?: string) => void
  decidingIds?: string[]
  onGrant?: (draft: GrantDraft) => void
  granting?: boolean
  onRevoke?: (member: TeamMember, grant: AccessGrant) => void
  revokingKeys?: string[]
  /**
   * Whether the deployment exposes a shift-end boundary the connected layer can
   * resolve `end_of_shift` to. Default true keeps the standalone fixtures/preview
   * showing the quick-pick; the connected board passes false when GET /config has
   * no single shift boundary, dropping end_of_shift for permanent + until +
   * relative quick-picks (the layer never fabricates a clock value).
   */
  shiftEndAvailable?: boolean
  /**
   * Unit switcher — when the viewer governs several units (an admin, a
   * multi-unit lead), the header offers a switch across them. Rendered only
   * with onSwitchUnit set AND more than one option; a single-unit lead keeps
   * the plain header.
   */
  unitOptions?: ResourceRef[]
  activeUnitId?: string
  onSwitchUnit?: (id: string) => void
  switchingUnit?: boolean
  now?: number
  className?: string
}

const grantKey = (userId: string, grant: AccessGrant) =>
  `${userId}:${grant.resource.type}:${grant.resource.id}`

export function TeamAccessBoard({
  team,
  members,
  requests,
  grantableSubjects,
  grantableResources,
  grantableRoles,
  onDecide,
  decidingIds,
  onGrant,
  granting,
  onRevoke,
  revokingKeys,
  shiftEndAvailable = true,
  unitOptions,
  activeUnitId,
  onSwitchUnit,
  switchingUnit,
  now,
  className,
}: TeamAccessBoardProps) {
  const { t } = useTranslation()
  const switchable = !!onSwitchUnit && (unitOptions?.length ?? 0) > 1

  return (
    <div className={cn('mx-auto w-full max-w-4xl space-y-6 p-4', className)}>
      <header className="rounded-[18px] border border-neutral-200 bg-white p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[13px] font-semibold uppercase tracking-wide text-neutral-500">
              {t('access.team.title')}
            </p>
            <h1 className="mt-1 truncate text-[21px] font-semibold tracking-[-0.01em] text-neutral-900">
              {team.label}
            </h1>
          </div>
          {switchable && (
            <div className="shrink-0">
              <label htmlFor="unit-switch" className="sr-only">
                {t('access.team.switchUnit')}
              </label>
              <select
                id="unit-switch"
                value={activeUnitId ?? ''}
                disabled={switchingUnit}
                onChange={(e) => {
                  if (e.target.value && e.target.value !== activeUnitId) {
                    onSwitchUnit?.(e.target.value)
                  }
                }}
                className="min-h-11 max-w-48 rounded-md border border-input bg-background px-3 py-2 text-[14px] ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50"
              >
                {(unitOptions ?? []).map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.label}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
        <p className="mt-2 text-[13px] text-neutral-500">
          {t('access.team.summary', {
            members: members.length,
            requests: requests.length,
          })}
        </p>
      </header>

      <ApprovalsInbox
        requests={requests}
        onDecide={onDecide}
        decidingIds={decidingIds}
      />

      {onGrant && (grantableSubjects?.length ?? 0) > 0 && (grantableResources?.length ?? 0) > 0 && (
        <GrantComposer
          subjects={grantableSubjects ?? []}
          resources={grantableResources ?? []}
          roles={grantableRoles ?? []}
          onGrant={onGrant}
          granting={granting}
          shiftEndAvailable={shiftEndAvailable}
        />
      )}

      <Roster members={members} onRevoke={onRevoke} revokingKeys={revokingKeys} now={now} />
    </div>
  )
}

// -----------------------------------------------------------------------------
// Approvals inbox
// -----------------------------------------------------------------------------

function ApprovalsInbox({
  requests,
  onDecide,
  decidingIds,
}: {
  requests: AccessRequest[]
  onDecide?: (requestId: string, decision: 'approve' | 'deny', justification?: string) => void
  decidingIds?: string[]
}) {
  const { t } = useTranslation()
  const [denyOpenId, setDenyOpenId] = useState<string | null>(null)
  const [denyText, setDenyText] = useState('')

  const openDeny = (id: string) => {
    setDenyOpenId(id)
    setDenyText('')
  }

  return (
    <section className="space-y-3">
      <h2 className="flex items-center gap-2 text-[15px] font-semibold text-neutral-900">
        {t('access.team.inbox.title')}
        {requests.length > 0 && (
          <span className="inline-flex min-w-5 items-center justify-center rounded-full bg-citation-50 px-1.5 text-[12px] font-semibold text-citation-700 tabular-nums">
            {requests.length}
          </span>
        )}
      </h2>
      {requests.length === 0 ? (
        <EmptyState
          icon={<Inbox aria-hidden="true" />}
          title={t('access.team.inbox.empty.title')}
          description={t('access.team.inbox.empty.description')}
        />
      ) : (
        <ul className="space-y-2">
          {requests.map((r) => {
            const Icon = resourceIcon(r.resource.type)
            const deciding = decidingIds?.includes(r.id)
            const denyOpen = denyOpenId === r.id
            return (
              <li
                key={r.id}
                className="rounded-[10px] border border-neutral-200 bg-white p-3"
              >
                <div className="flex flex-wrap items-start gap-x-3 gap-y-2">
                  <PersonAvatar user={r.requester} size="sm" />
                  <div className="min-w-0 flex-1">
                    <p className="text-[15px] font-semibold text-neutral-900">
                      {r.requester.name}
                    </p>
                    <p className="mt-0.5 inline-flex flex-wrap items-center gap-1.5 text-[13px] text-neutral-600">
                      <Icon className="h-3.5 w-3.5 shrink-0 text-neutral-400" aria-hidden="true" />
                      <span className="truncate">{r.resource.label}</span>
                      {r.relation && <RoleBadge role={r.relation} />}
                    </p>
                    {r.justification && (
                      <p className="mt-1.5 rounded-[8px] bg-neutral-50 px-2.5 py-1.5 text-[13px] leading-relaxed text-neutral-700">
                        {r.justification}
                      </p>
                    )}
                  </div>
                  {onDecide && !denyOpen && (
                    <div className="flex shrink-0 items-center gap-1.5">
                      <Button
                        type="button"
                        size="pill"
                        disabled={deciding}
                        onClick={() => onDecide(r.id, 'approve')}
                        className="min-h-11"
                      >
                        {deciding ? t('access.team.inbox.deciding') : t('access.team.inbox.approve')}
                      </Button>
                      <Button
                        type="button"
                        size="pill"
                        variant="ghost"
                        disabled={deciding}
                        onClick={() => openDeny(r.id)}
                        className="min-h-11 text-neutral-500 hover:bg-destructive/5 hover:text-destructive"
                      >
                        {t('access.team.inbox.deny')}
                      </Button>
                    </div>
                  )}
                </div>

                {onDecide && denyOpen && (
                  <div className="mt-3 space-y-2 rounded-[8px] border border-neutral-200 bg-neutral-50 p-3">
                    <label
                      htmlFor={`deny-${r.id}`}
                      className="block text-[13px] font-semibold text-neutral-700"
                    >
                      {t('access.team.inbox.denyReasonLabel')}
                    </label>
                    <Textarea
                      id={`deny-${r.id}`}
                      value={denyText}
                      onChange={(e) => setDenyText(e.target.value)}
                      placeholder={t('access.team.inbox.denyReasonPlaceholder')}
                      className="min-h-[64px] bg-white text-[15px]"
                    />
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        size="pill"
                        variant="outline"
                        disabled={deciding}
                        onClick={() => {
                          onDecide(r.id, 'deny', denyText.trim() || undefined)
                          setDenyOpenId(null)
                        }}
                        className="min-h-11 border-destructive/30 text-destructive hover:border-destructive/50 hover:bg-destructive/5 hover:text-destructive"
                      >
                        {deciding ? t('access.team.inbox.deciding') : t('access.team.inbox.confirmDeny')}
                      </Button>
                      <Button
                        type="button"
                        size="pill"
                        variant="ghost"
                        onClick={() => setDenyOpenId(null)}
                        className="min-h-11"
                      >
                        {t('access.team.inbox.cancel')}
                      </Button>
                    </div>
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}

// -----------------------------------------------------------------------------
// Grant composer
// -----------------------------------------------------------------------------

type ExpiryChoice = 'end_of_shift' | 'permanent' | 'until'

// Relative expiry quick-picks (hours) offered when no shift boundary is
// configured — a lead's "cover the next few hours" without opening a date
// picker. Presentation convenience, not a domain limit (cf. ROSTER_GRANT_CAP).
const RELATIVE_EXPIRY_HOURS = [4, 8]

/** Format an epoch-ms instant as a `datetime-local` input value (local zone). */
function toDatetimeLocal(ms: number): string {
  const d = new Date(ms)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function GrantComposer({
  subjects,
  resources,
  roles,
  onGrant,
  granting,
  shiftEndAvailable,
}: {
  subjects: GrantSubjectOption[]
  resources: ResourceRef[]
  roles: string[]
  onGrant: (draft: GrantDraft) => void
  granting?: boolean
  shiftEndAvailable: boolean
}) {
  const { t } = useTranslation()
  const [subjectId, setSubjectId] = useState('')
  const [resourceId, setResourceId] = useState('')
  const [role, setRole] = useState(roles[0] ?? '')
  // "End of shift" is the safe temporary grant a lead reaches for — but only
  // when the deployment has a shift boundary to resolve it to; otherwise default
  // to a permanent grant the lead narrows with the until / relative picks.
  const expiryChoices: ExpiryChoice[] = shiftEndAvailable
    ? ['end_of_shift', 'permanent', 'until']
    : ['permanent', 'until']
  const [expiryChoice, setExpiryChoice] = useState<ExpiryChoice>(
    shiftEndAvailable ? 'end_of_shift' : 'permanent',
  )
  const [untilAt, setUntilAt] = useState('')

  const pickRelative = (hours: number) => {
    setExpiryChoice('until')
    setUntilAt(toDatetimeLocal(Date.now() + hours * 60 * 60 * 1000))
  }

  const members = subjects.filter((s) => s.type === 'user')
  const groups = subjects.filter((s) => s.type === 'group')

  const buildExpiry = (): GrantExpiry | null => {
    if (expiryChoice === 'permanent') return { kind: 'permanent' }
    if (expiryChoice === 'end_of_shift') return { kind: 'end_of_shift' }
    if (!untilAt) return null
    return { kind: 'until', at: new Date(untilAt).toISOString() }
  }

  const subject = subjects.find((s) => s.id === subjectId)
  const resource = resources.find((r) => r.id === resourceId)
  const expiry = buildExpiry()
  const ready = !!subject && !!resource && !!role && !!expiry && !granting

  const submit = () => {
    if (!subject || !resource || !role || !expiry) return
    onGrant({ subject, resource, role, expiry })
    setSubjectId('')
    setResourceId('')
    setRole(roles[0] ?? '')
    setExpiryChoice(shiftEndAvailable ? 'end_of_shift' : 'permanent')
    setUntilAt('')
  }

  const selectClass =
    'min-h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-[15px] ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'

  return (
    <section className="space-y-3">
      <h2 className="flex items-center gap-2 text-[15px] font-semibold text-neutral-900">
        <UserRoundPlus className="h-4 w-4 text-neutral-400" aria-hidden="true" />
        {t('access.team.composer.title')}
      </h2>
      <div className="space-y-3 rounded-[18px] border border-neutral-200 bg-white p-4">
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="space-y-1">
            <label htmlFor="grant-subject" className="block text-[13px] font-semibold text-neutral-500">
              {t('access.team.composer.who')}
            </label>
            <select
              id="grant-subject"
              value={subjectId}
              onChange={(e) => setSubjectId(e.target.value)}
              className={selectClass}
            >
              <option value="">{t('access.team.composer.whoPlaceholder')}</option>
              {members.length > 0 && (
                <optgroup label={t('access.team.composer.membersGroup')}>
                  {members.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.label}
                    </option>
                  ))}
                </optgroup>
              )}
              {groups.length > 0 && (
                <optgroup label={t('access.team.composer.rolesGroup')}>
                  {groups.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.label}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
          </div>

          <div className="space-y-1">
            <label htmlFor="grant-resource" className="block text-[13px] font-semibold text-neutral-500">
              {t('access.team.composer.what')}
            </label>
            <select
              id="grant-resource"
              value={resourceId}
              onChange={(e) => setResourceId(e.target.value)}
              className={selectClass}
            >
              <option value="">{t('access.team.composer.whatPlaceholder')}</option>
              {resources.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1">
            <label htmlFor="grant-role" className="block text-[13px] font-semibold text-neutral-500">
              {t('access.team.composer.role')}
            </label>
            <select
              id="grant-role"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className={selectClass}
            >
              {roles.length === 0 && <option value="">—</option>}
              {roles.map((r) => (
                <option key={r} value={r}>
                  {t(roleKey(r), { defaultValue: r })}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Expiry — "end of shift" is the default quick-pick. */}
        <div className="space-y-1.5">
          <span className="block text-[13px] font-semibold text-neutral-500">
            {t('access.team.composer.expiry')}
          </span>
          <div className="flex flex-wrap gap-1.5">
            {expiryChoices.map((choice) => (
              <button
                key={choice}
                type="button"
                onClick={() => setExpiryChoice(choice)}
                aria-pressed={expiryChoice === choice}
                className={cn(
                  'min-h-11 rounded-full border px-4 text-[13px] font-semibold transition active:scale-95',
                  expiryChoice === choice
                    ? 'border-primary bg-primary-50 text-primary-700'
                    : 'border-neutral-200 bg-white text-neutral-600',
                )}
              >
                {t(`access.team.composer.expiryChoice.${choice}`)}
              </button>
            ))}
            {/* Relative quick-picks stand in for "end of shift" when no shift
                boundary is configured — each fills the until value with now+Nh. */}
            {!shiftEndAvailable &&
              RELATIVE_EXPIRY_HOURS.map((hours) => (
                <button
                  key={`plus-${hours}`}
                  type="button"
                  onClick={() => pickRelative(hours)}
                  className="min-h-11 rounded-full border border-neutral-200 bg-white px-4 text-[13px] font-semibold text-neutral-600 transition active:scale-95"
                >
                  {t('access.team.composer.relativeHours', { hours })}
                </button>
              ))}
          </div>
          {expiryChoice === 'until' && (
            <input
              type="datetime-local"
              value={untilAt}
              onChange={(e) => setUntilAt(e.target.value)}
              aria-label={t('access.team.composer.untilLabel')}
              className={cn(selectClass, 'mt-1')}
            />
          )}
        </div>

        <div className="pt-1">
          <Button
            type="button"
            size="pill"
            disabled={!ready}
            onClick={submit}
            className="min-h-11"
          >
            {granting ? t('access.team.composer.granting') : t('access.team.composer.grant')}
          </Button>
        </div>
      </div>
    </section>
  )
}

// -----------------------------------------------------------------------------
// Roster
// -----------------------------------------------------------------------------

const NO_ROLE = '__no_role__'
// How many grants to show on a member card before collapsing the rest into a
// "+N more" tally — a presentation cap, not a domain limit.
const ROSTER_GRANT_CAP = 4

function Roster({
  members,
  onRevoke,
  revokingKeys,
  now,
}: {
  members: TeamMember[]
  onRevoke?: (member: TeamMember, grant: AccessGrant) => void
  revokingKeys?: string[]
  now?: number
}) {
  const { t } = useTranslation()

  const groups = useMemo(() => {
    const map = new Map<string, { label: string; members: TeamMember[] }>()
    for (const m of members) {
      const role = m.roles[0]
      const key = role ?? NO_ROLE
      const label = role
        ? t(roleKey(role), { defaultValue: role })
        : t('access.team.roster.otherGroup')
      const bucket = map.get(key)
      if (bucket) bucket.members.push(m)
      else map.set(key, { label, members: [m] })
    }
    return [...map.values()].sort((a, b) => {
      if (a.label === t('access.team.roster.otherGroup')) return 1
      if (b.label === t('access.team.roster.otherGroup')) return -1
      return a.label.localeCompare(b.label)
    })
  }, [members, t])

  return (
    <section className="space-y-3">
      <h2 className="flex items-center gap-2 text-[15px] font-semibold text-neutral-900">
        <Users className="h-4 w-4 text-neutral-400" aria-hidden="true" />
        {t('access.team.roster.title')}
      </h2>
      {members.length === 0 ? (
        <EmptyState
          icon={<Users aria-hidden="true" />}
          title={t('access.team.roster.empty.title')}
          description={t('access.team.roster.empty.description')}
        />
      ) : (
        <div className="space-y-5">
          {groups.map((group) => (
            <div key={group.label} className="space-y-2">
              <h3 className="text-[13px] font-semibold uppercase tracking-wide text-neutral-500">
                {group.label}
                <span className="ml-1.5 font-normal normal-case tabular-nums text-neutral-400">
                  {group.members.length}
                </span>
              </h3>
              <div className="grid gap-3 sm:grid-cols-2">
                {group.members.map((m) => (
                  <MemberCard
                    key={m.user.id}
                    member={m}
                    onRevoke={onRevoke}
                    revokingKeys={revokingKeys}
                    now={now}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  )
}

function MemberCard({
  member,
  onRevoke,
  revokingKeys,
  now,
}: {
  member: TeamMember
  onRevoke?: (member: TeamMember, grant: AccessGrant) => void
  revokingKeys?: string[]
  now?: number
}) {
  const { t } = useTranslation()
  const shownGrants = member.grants.slice(0, ROSTER_GRANT_CAP)
  const extraGrants = member.grants.length - shownGrants.length

  return (
    <article className="space-y-3 rounded-[18px] border border-neutral-200 bg-white p-4">
      <div className="flex items-center gap-3">
        <PersonAvatar user={member.user} size="lg" />
        <div className="min-w-0">
          <p className="truncate text-[15px] font-semibold text-neutral-900">{member.user.name}</p>
          {member.user.specialisation && (
            <p className="truncate text-[13px] text-neutral-500">{member.user.specialisation}</p>
          )}
        </div>
      </div>

      {member.roles.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {member.roles.map((r) => (
            <RoleBadge key={r} role={r} />
          ))}
        </div>
      )}

      {/* Expiring grants surface first — the thing a lead must act on. */}
      {member.expiring.length > 0 && (
        <div className="space-y-1.5 rounded-[10px] bg-citation-50/60 p-2.5">
          <p className="inline-flex items-center gap-1 text-[12px] font-semibold text-citation-700">
            <AlarmClock className="h-3.5 w-3.5" aria-hidden="true" />
            {t('access.team.roster.expiringSoon')}
          </p>
          {member.expiring.map((g) => (
            <GrantRow
              key={grantKey(member.user.id, g)}
              member={member}
              grant={g}
              onRevoke={onRevoke}
              revoking={revokingKeys?.includes(grantKey(member.user.id, g))}
              now={now}
            />
          ))}
        </div>
      )}

      {member.grants.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-[12px] font-semibold uppercase tracking-wide text-neutral-400">
            {t('access.team.roster.grants', { count: member.grants.length })}
          </p>
          {shownGrants.map((g) => (
            <GrantRow
              key={grantKey(member.user.id, g)}
              member={member}
              grant={g}
              onRevoke={onRevoke}
              revoking={revokingKeys?.includes(grantKey(member.user.id, g))}
              now={now}
            />
          ))}
          {extraGrants > 0 && (
            <p className="text-[12px] text-neutral-400">
              {t('access.team.roster.moreGrants', { count: extraGrants })}
            </p>
          )}
        </div>
      )}

      {member.open_requests.length > 0 && (
        <p className="inline-flex items-center gap-1 text-[12px] font-semibold text-neutral-500">
          <Inbox className="h-3.5 w-3.5" aria-hidden="true" />
          {t('access.team.roster.openRequests', { count: member.open_requests.length })}
        </p>
      )}
    </article>
  )
}

function GrantRow({
  member,
  grant,
  onRevoke,
  revoking,
  now,
}: {
  member: TeamMember
  grant: AccessGrant
  onRevoke?: (member: TeamMember, grant: AccessGrant) => void
  revoking?: boolean
  now?: number
}) {
  const { t } = useTranslation()
  const Icon: LucideIcon = resourceIcon(grant.resource.type)
  return (
    <div className="flex items-center gap-2">
      <Icon className="h-3.5 w-3.5 shrink-0 text-neutral-400" aria-hidden="true" />
      <span className="min-w-0 flex-1 truncate text-[13px] text-neutral-800">
        {grant.resource.label}
      </span>
      <span className="shrink-0 text-[12px] text-neutral-500">
        {t(roleKey(grant.role), { defaultValue: grant.role })}
      </span>
      {grant.expires_at && <ExpiryChip expiresAt={grant.expires_at} now={now} />}
      <SourceChip source={grant.source} className="hidden sm:inline-flex" />
      {onRevoke && (
        <button
          type="button"
          onClick={() => onRevoke(member, grant)}
          disabled={revoking}
          aria-label={t('access.team.roster.revoke')}
          className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-neutral-400 transition active:scale-95 hover:text-destructive disabled:opacity-50"
        >
          <X className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      )}
    </div>
  )
}
